import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Check, Loader2, Crown, ArrowLeft } from "lucide-react";
import Header from "@/components/Header";

const BENEFITS = [
  "Cake Pop Baker Map — your pin visible to buyers",
  "Marketplace — list products in a full storefront",
  "AI Baker's Tools — Crumb Analyzer, Dietary Adaptor, Recipe Scaler & more",
  "Business Tools — Costing, Invoices, Inventory, Labels, Ledger",
  "Marketing Tools — Logo Maker, Social Posts, Newsletters, Photo Enhancer",
];

export default function Upgrade() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(false);
  // Track which session_ids we've already handled to prevent duplicate toasts when the effect
  // re-runs (e.g. after refresh() updates the user object).
  const handledSessions = useRef(new Set());

  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId || !user || user === false) return;
    if (handledSessions.current.has(sessionId)) return;
    handledSessions.current.add(sessionId);
    let cancelled = false;
    let attempts = 0;
    setPolling(true);
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/subscription/checkout-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          // Clean URL & stop polling FIRST so the button state clears immediately,
          // even if the subsequent refresh() causes the effect to re-run.
          setPolling(false);
          nav("/upgrade", { replace: true });
          if (!data.already_processed) {
            toast.success("Welcome to Premium! 🎉");
          }
          if (refresh) await refresh();
          return;
        }
        if (data.status === "expired") {
          toast.error("Checkout expired. Please try again.");
          setPolling(false);
          return;
        }
        if (attempts++ < 8) setTimeout(poll, 2000);
        else setPolling(false);
      } catch (e) { setPolling(false); }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, user]);

  const startTrial = async () => {
    if (!user || user === false) { nav("/login?next=/upgrade"); return; }
    setBusy(true);
    try {
      await api.post("/subscription/start-trial");
      toast.success("14-day free trial started 🎂");
      if (refresh) await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const checkout = async () => {
    if (!user || user === false) { nav("/login?next=/upgrade"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy(false);
    }
  };


  const cancelSub = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep premium access until the end of your current billing period.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/subscription/cancel");
      toast.success(`Subscription set to cancel${data.current_period_end ? ` on ${new Date(data.current_period_end).toLocaleDateString()}` : " at period end"}.`);
      if (refresh) await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const resumeSub = async () => {
    setBusy(true);
    try {
      await api.post("/subscription/resume");
      toast.success("Subscription resumed — thanks for staying! 🎂");
      if (refresh) await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const sub = user?.subscription_status;
  const hasPremium = user?.is_premium;
  const cancelScheduled = !!user?.subscription_cancel_at_period_end;
  const periodEnd = user?.subscription_current_period_end;
  const periodEndStr = periodEnd ? new Date(periodEnd).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null;

  return (
    <>
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <button onClick={() => nav(-1)} className="text-sm text-[#857163] hover:text-[#3B2C24] inline-flex items-center gap-1 mb-4" data-testid="upgrade-back"><ArrowLeft className="w-3.5 h-3.5" /> Back</button>
        <div className="cp-card p-6 sm:p-10 text-center" data-testid="upgrade-card">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]"><Crown className="w-3.5 h-3.5" style={{ color: "#F4A83B" }} /><span>Premium</span></div>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-2">Unlock everything <span style={{ color: "#FF7E67" }}>Cake Pop Talk</span> can do</h1>
          <p className="mt-3 text-[#857163] max-w-xl mx-auto">One low subscription unlocks every baker tool, business calculator, marketing generator, marketplace listing, and your pin on the Baker Map.</p>

          <div className="mt-6 flex items-baseline justify-center gap-1">
            <span className="text-5xl font-heading font-bold text-[#3B2C24]">$7</span>
            <span className="text-[#857163]">/month</span>
          </div>
          <div className="text-xs text-[#857163] mt-1">14-day free trial · cancel anytime</div>

          <ul className="mt-6 text-left inline-block space-y-2 max-w-md">
            {BENEFITS.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-[#3B2C24]">
                <Check className="w-4 h-4 shrink-0 mt-0.5" style={{ color: "#8EAC50" }} />
                <span>{b}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            {sub === "none" && (
              <button onClick={startTrial} disabled={busy} className="cp-btn-ghost inline-flex items-center gap-1.5" data-testid="start-trial-btn">
                <Sparkles className="w-4 h-4" style={{ color: "#F4A83B" }} /> Start 14-day free trial
              </button>
            )}
            {sub === "active" ? (
              cancelScheduled ? (
                <button
                  onClick={resumeSub}
                  disabled={busy}
                  className="px-8 py-3 rounded-full text-white font-semibold text-base hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-2"
                  style={{ background: "linear-gradient(135deg, #8EAC50, #A8C86A)" }}
                  data-testid="resume-sub-btn"
                >
                  {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Working…</> : "Resume subscription"}
                </button>
              ) : (
                <button
                  onClick={cancelSub}
                  disabled={busy}
                  className="px-8 py-3 rounded-full font-semibold text-base border-2 disabled:opacity-60 inline-flex items-center gap-2 text-[#3B2C24] border-[#E7DED5] hover:bg-[#FBF6F1]"
                  data-testid="cancel-sub-btn"
                >
                  {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Working…</> : "Cancel subscription"}
                </button>
              )
            ) : (
              <button
                onClick={checkout}
                disabled={busy || polling}
                className="px-8 py-3 rounded-full text-white font-semibold text-base hover:opacity-90 disabled:opacity-60 inline-flex items-center gap-2"
                style={{ background: "linear-gradient(135deg, #FF7E67, #FF9E85)" }}
                data-testid="checkout-btn"
              >
                {polling ? <><Loader2 className="w-5 h-5 animate-spin" /> Verifying payment…</> :
                  busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting…</> :
                  "Start subscription — $7/mo"}
              </button>
            )}
          </div>

          {/* Dev QA helper removed — real subscription flow is live */}

          {hasPremium && sub !== "expired" && sub !== "none" && (
            <div className="mt-6 text-sm text-[#5F7A2E] bg-[#8EAC50]/10 rounded-2xl p-3 inline-block max-w-md" data-testid="premium-status">
              {sub === "trial" ? (
                <>You&apos;re on your free trial — {user.subscription_days_left ?? 0} days left. Subscribe any time to keep everything after your trial.</>
              ) : cancelScheduled ? (
                <>Your subscription is set to cancel{periodEndStr ? ` on ${periodEndStr}` : " at the end of the current period"}. You&apos;ll keep Premium access until then.</>
              ) : (
                <>You&apos;re a Premium member{periodEndStr ? ` — next renewal on ${periodEndStr}` : ""}. Thank you for supporting Cake Pop Talk! 🎉</>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-xs text-[#857163] mt-4">
          Have questions? <Link to="/messages" className="underline">Contact support</Link>.
        </p>
      </main>
    </>
  );
}
